import { BaseElementWithoutShadowDOM } from '@theme/base-element'
import deferredMediaContext from '@theme/context-deferred-media'
import { ContextConsumer } from '@theme/vendor-lit'

const DEFERRED_MEDIA_EVENTS = {
  PAUSE: 'deferred-media::pause',
  PLAY: 'deferred-media::play',
  MUTE: 'deferred-media::mute',
  UNMUTE: 'deferred-media::unmute',
}

const YT_PLAYER_STATE = {
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
}
export class DeferredMedia extends BaseElementWithoutShadowDOM {
  static get properties() {
    return {
      host: { type: String },
      mediaType: { type: String, attribute: 'media-type' },
      autoplay: { type: Boolean },
      controls: { type: Boolean },
      loop: { type: Boolean },
      muted: { type: Boolean },
    }
  }

  get poster() {
    return this.updateComplete.then(
      () =>
        this.renderRoot?.querySelector('[id^="Deferred-Poster-Modal-"]') ?? null
    )
  }

  get muteButton() {
    return this.updateComplete.then(
      () =>
        this.renderRoot?.querySelector('[id^="Deferred-Mute-Button-"]') ?? null
    )
  }

  get playButton() {
    return this.updateComplete.then(
      () =>
        this.renderRoot?.querySelector('[id^="Deferred-Play-Button-"]') ?? null
    )
  }

  get template() {
    return this.updateComplete.then(
      () => this.renderRoot?.querySelector('template') ?? null
    )
  }

  get video() {
    return this.updateComplete.then(
      () => this.renderRoot?.querySelector('video') ?? null
    )
  }

  get iframe() {
    return this.updateComplete.then(
      () => this.renderRoot?.querySelector('iframe') ?? null
    )
  }

  constructor() {
    super()

    this.playing = false
    this.loaded = false
    new ContextConsumer(this, {
      context: deferredMediaContext,
      subscribe: true,
      callback: (value) => {
        this.activeDeferredMedia = value
      },
    })

    this.handlePosterClick = this.handlePosterClick.bind(this)
    this.handleMuteClick = this.handleMuteClick.bind(this)
    this.handlePlayButtonClick = this.handlePlayButtonClick.bind(this)
    this.handleIntersection = this.handleIntersection.bind(this)
  }

  get isInlineHTML5LoopingVideo() {
    return this.mediaType === 'video' && this.loop && this.autoplay
  }

  firstUpdated(_changedProperties) {
    super.firstUpdated(_changedProperties)

    if (this.isInlineHTML5LoopingVideo) {
      this.#loadContent(false, false)
    }
  }

  async setupIntersectionObserver() {
    const observerOptions = {
      root: null, // The viewport
      threshold: 0.5, // 50% of the element must be visible to trigger the observer
    }

    // Initialize the observer
    this.observer = new IntersectionObserver(
      this.handleIntersection,
      observerOptions
    )

    const $video = await this.video
    if ($video) {
      this.observer.observe($video)
    }

    const $iframe = await this.iframe
    if ($iframe) {
      this.observer.observe($iframe)
    }
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      entry.isIntersecting ? this.#play() : this.#_pause()
    })
  }

  updated(_changedProperties) {
    super.updated(_changedProperties)
    if (this.isInlineHTML5LoopingVideo) return
    if (this.activeDeferredMedia === this) {
      this.#loadContent()
    } else {
      this.pause(true)
    }
  }

  async connectedCallback() {
    super.connectedCallback()
    const poster = await this.poster
    const muteButton = await this.muteButton
    const playButton = await this.playButton
    poster?.addEventListener('click', this.handlePosterClick)
    muteButton?.addEventListener('click', this.handleMuteClick)
    playButton?.addEventListener('click', this.handlePlayButtonClick)

    this.setupIntersectionObserver()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.observer) {
      this.observer.disconnect()
    }
  }

  play() {
    if (this.playing) return
    this.$emit(DEFERRED_MEDIA_EVENTS.PLAY)
    this.playing = true
  }

  pause(skipEvent = false) {
    if (!skipEvent) {
      if (!this.playing) return
      this.$emit(DEFERRED_MEDIA_EVENTS.PAUSE)
    }

    this.playing = false
    if (!this.isInlineHTML5LoopingVideo || !skipEvent) {
      this.#_pause()
    }
  }

  handlePosterClick(event) {
    event.preventDefault()
    this.play()
  }

  async handleMuteClick(event) {
    event.preventDefault()
    const $video = await this.video
    if (!$video) return

    this.muted = !this.muted
    $video.muted = this.muted

    if (this.muted) {
      this.setAttribute('muted', 'true')
      this.$emit(DEFERRED_MEDIA_EVENTS.MUTE)
    } else {
      this.removeAttribute('muted')
      this.$emit(DEFERRED_MEDIA_EVENTS.UNMUTE)
    }
  }
  async handlePlayButtonClick(event) {
    event.preventDefault()
    const $video = await this.video
    if (!$video) return

    if (this.playing) {
      this.removeAttribute('playing')
      await this.#togglePlayState(false)
    } else {
      this.setAttribute('playing', 'true')
      await this.#togglePlayState(true)
    }
  }

  #_pause() {
    return this.#togglePlayState(false)
  }

  async #togglePlayState(play = true) {
    if (!this.loaded) return
    if (this.mediaType === 'video') {
      const $video = await this.video
      play ? $video?.play() : $video?.pause()
    } else if (this.mediaType === 'external_video') {
      if (this.host === 'youtube') {
        play ? this.player?.playVideo() : this.player?.pauseVideo()
      } else if (this.host === 'vimeo') {
        play ? this.player?.play() : this.player?.pause()
      }
    }
  }

  #play() {
    return this.#togglePlayState(true)
  }

  async #loadContent(focus = true, replaceToggle = true) {
    if (!this.loaded) {
      this.loaded = true
      const content = document.createElement('div')
      const $template = await this.template
      $template &&
        $template.content.firstElementChild &&
        content.appendChild($template.content.firstElementChild.cloneNode(true))

      const deferredElement = content.querySelector('video, iframe')
      if (!deferredElement) return
      if (replaceToggle) {
        this.innerHTML = deferredElement.outerHTML
      } else {
        this.appendChild(deferredElement)
      }

      if (focus) deferredElement?.focus()
      if (
        deferredElement &&
        deferredElement.nodeName == 'VIDEO' &&
        deferredElement.getAttribute('autoplay')
      ) {
        deferredElement.play()
      }
      this._initListners()
    } else {
      this.#play()
    }
  }

  async _attachHTMLVideoEvents() {
    const $video = await this.video

    if (!$video) return
    $video.addEventListener('pause', () => {
      this.playing = false
      this.pause()
    })

    $video.addEventListener('play', () => {
      this.play()
    })
  }

  async _attachYoutubeEvents() {
    const { default: YT } = await import('@theme/vendor-youtube-player')
    const iframe = await this.iframe
    if (!iframe) return

    this.player = YT(iframe, {
      playerVars: {
        autoplay: this.autoplay ? 1 : 0,
        controls: this.controls ? 1 : 0,
        loop: this.loop ? 1 : 0,
        playsinline: 1,
        rel: 0,
        modestbranding: 1,
      },
    })

    this.player.getPlayerState().then((state) => {
      this.playing = state === YT_PLAYER_STATE.PLAYING
    })

    this.player.on('stateChange', (event) => {
      switch (event.data) {
        case YT_PLAYER_STATE.PLAYING:
          this.play()
          break
        case YT_PLAYER_STATE.PAUSED:
        case YT_PLAYER_STATE.ENDED:
          this.playing = false
          this.pause()
          break
      }
    })
  }

  async _attachVimeoEvents() {
    const Vimeo = await import('@theme/vendor-vimeo-player')
    const iframe = await this.iframe
    if (!iframe) return
    const player = new Vimeo(iframe, {
      autoplay: this.autoplay,
      controls: this.controls,
      loop: this.loop,
      muted: this.muted,
      byline: false,
      title: false,
    })

    player.on('play', () => {
      this.play()
    })

    player.on('pause', () => {
      this.playing = false
      this.pause()
    })
  }

  _initListners() {
    if (this.mediaType === 'video') {
      this._attachHTMLVideoEvents()
    }

    if (this.mediaType === 'external_video') {
      this.host === 'youtube' && this._attachYoutubeEvents()
      this.host === 'vimeo' && this._attachVimeoEvents()
    }
  }
}

customElements.define('deferred-media', DeferredMedia)
